import EditorContent from "../common/EditorContent";
import { useLineItemRequests } from "../../hooks/useLineItemRequests";
import { appendVariantLineItems, sortedVariantLineItems, removedVariantIds, variantLineItemIdentity, variantLineItemsMap } from "../../helper_functions/variantLineItems";
import React, { useState, useRef, useMemo } from "react";
import {
  LegacyCard,
  Page,
  Badge,
  TextField,
  Label,
  Button,
  Box,
  Icon,
  LegacyStack,
  FormLayout,
  PageActions,
  IndexTable,
  EmptyState,
  Layout,
} from "@shopify/polaris";
import { XIcon, ExportIcon, ImportIcon } from "@shopify/polaris-icons";
import AddVariantDialog from "../common/AddVariantDialog";
import DeleteConfirmationDialog from "../common/DeleteConfirmationDialog";
import CustomDatePicker from "../common/CustomDatePicker/CustomDatePicker";
import {
  updateTransferOrder,
  previewUnsavedTransferOrderLineItems,
  deleteTransferOrder,
  importTransferOrderLineItems,
  downloadFile,
  useUser,
} from "../../api_utils/requests";
import ConfirmationDialog from "../common/ConfirmationDialog";
import {
  assertNumber,
  assertString,
  commaDelimiter,
  extractMessageFromError,
  formatMoney,
} from "../../helper_functions/utils";
import HorseVariant from "../common/HorseVariant/HorseVariant";
import { ImportFile } from "../common/ImportFile";
import { ErrorBanner } from "../common/ErrorBanner";
import { Footer } from "../common/Footer";
import type { HorseLocation, TransferOrder, TransferOrderLineItem, TransferOrderResponse, LineItemVariant } from "../../api_utils/types";
import { TRANSFER_ORDER_HELP_PAGE_URL } from "../../constants";
import TableControls from "../common/TableControls";
import { useVisibleColumns } from "../../hooks/useVisibleColumns";
import SaveBar from "../common/SaveBar";
import { useNavigate } from "react-router-dom";
import HorseLink from "../common/HorseLink";

type TransferOrderLineItems = Record<number, TransferOrderLineItem & { sortIndex: number }>;

const notValidateQuantities = (formattedTransferOrderLineItems: TransferOrderLineItems): boolean =>
  Object.values(formattedTransferOrderLineItems).some(({ quantity_sent }) => !quantity_sent || quantity_sent === 0);

const UPDATE_TRANSFER_ORDER = 1;
const MARK_AS_SENT = 2;

const SUCCESS_MESSAGES = {
  1: "Save successful",
  2: "Marked as sent",
};

const initialQuantity = { more_than: "0", less_than: undefined };

const getPayload = (lineItems: TransferOrderLineItems) =>
  sortedVariantLineItems(lineItems).map((lineItem) => ({
    ...variantLineItemIdentity(lineItem),
    quantity_sent: lineItem.quantity_sent,
  }));

const optionalColumns = [{ id: "warehouse_location", title: "Warehouse location" }] as const;

export default function Index({
  origin,
  destination,
  transferOrder: initialTransferOrder,
  lineItems: initialLineItems,
  onSave,
}: {
  readonly origin: HorseLocation;
  readonly destination: HorseLocation;
  readonly transferOrder: TransferOrder;
  readonly lineItems: TransferOrderLineItem[];
  readonly onSave: (response: TransferOrderResponse) => void;
}): React.ReactElement {
  const defaultArrivalDate = useMemo(() => {
    const date = new Date();
    date.setMonth(date.getMonth() + 1);
    return date;
  }, []);
  const initialTransferOrderWithEstimatedArrivalDate = {
    ...initialTransferOrder,
    estimated_arrival_date: initialTransferOrder.estimated_arrival_date
      ? new Date(initialTransferOrder.estimated_arrival_date)
      : defaultArrivalDate,
  };
  const [transferOrder, setTransferOrder] = useState<TransferOrder>(initialTransferOrderWithEstimatedArrivalDate);
  const [originalTransferOrder, setOriginalTransferOrder] = useState<TransferOrder>(
    initialTransferOrderWithEstimatedArrivalDate,
  );
  const [formattedTransferOrderLineItems, setFormattedTransferOrderLineItems] = useState<TransferOrderLineItems>(
    variantLineItemsMap(initialLineItems),
  );
  const originalLineItems = useRef<TransferOrderLineItems>(variantLineItemsMap(initialLineItems));
  const [isDirty, setIsDirty] = useState(false);
  const [loading, setLoading] = useState(false);
  const [importCSVModalIsOpen, setImportCSVModalIsOpen] = useState(false);
  const [markAsSentModalActive, setMarkAsSentModalActive] = useState(false);

  const [deletionDialogueActive, setDeletionDialogueActive] = useState(false);
  const [addVariantModalActive, setAddVariantModalActive] = useState(false);

  const [errorMessage, setErrorMessage] = useState("");
  const { visibleColumns, selectedColumns, setSelectedColumns } = useVisibleColumns(
    "transfer-orders-draft",
    optionalColumns,
  );

  const { request, cancel } = useLineItemRequests(setErrorMessage);
  const acceptResponse = (response: TransferOrderResponse): void => {
    const savedTransferOrder = {
      ...response.transferOrder,
      estimated_arrival_date: response.transferOrder.estimated_arrival_date ? new Date(response.transferOrder.estimated_arrival_date) : defaultArrivalDate,
    };
    setTransferOrder(savedTransferOrder);
    setOriginalTransferOrder(savedTransferOrder);
    const savedLineItems = variantLineItemsMap(response.lineItems);
    originalLineItems.current = savedLineItems;
    setFormattedTransferOrderLineItems(savedLineItems);
    setIsDirty(false);
    onSave(response);
  };

  const {
    data: {
      user: { currency: userCurrency },
    },
  } = useUser();

  const handleDateChange = (date: Date): void => {
    setTransferOrder({ ...transferOrder, estimated_arrival_date: date });
    setIsDirty(true);
  };

  const handleLabelChange = (label: string): void => {
    setTransferOrder({ ...transferOrder, label });
    setIsDirty(true);
  };

  const handleQuantityChange = (quantity_sent: string, id: string): void => {
    const cleanQuantitySent = quantity_sent?.replace(/[^\d.-]/g, "");
    setFormattedTransferOrderLineItems({
      ...formattedTransferOrderLineItems,
      [id]: {
        ...formattedTransferOrderLineItems[id],
        quantity_sent: cleanQuantitySent,
      } as TransferOrderLineItems[number],
    });
    setIsDirty(true);
  };

  const handleMarkAsSent = (): void => {
    if (notValidateQuantities(formattedTransferOrderLineItems)) {
      setErrorMessage("Quantity must be greater or equal to 1");
      return;
    }
    cancel();
    setLoading(true);
    const payload = {
      transfer_order: {
        transfer_order_horse_variants_attributes: getPayload(formattedTransferOrderLineItems),
        removed_horse_variant_ids: removedVariantIds(originalLineItems.current, formattedTransferOrderLineItems),
        state: "sent",
      },
    };
    updateTransferOrder(transferOrder.id, payload)
      .then((response) => {
        shopify.toast.show(SUCCESS_MESSAGES[MARK_AS_SENT]);
        acceptResponse(response);
      })
      .catch((err: unknown) => {
        Rollbar.error(err, payload);
        const message = extractMessageFromError(err);
        setErrorMessage(message);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const removeVariants = (horseVariantIds: number[]): void => {
    const removedIds = new Set(horseVariantIds);
    setFormattedTransferOrderLineItems((lineItems) => Object.fromEntries(
      Object.entries(lineItems).filter(([id]) => !removedIds.has(Number(id))),
    ));
    setIsDirty(true);
  };

  const addVariants = (horseVariants: LineItemVariant[]): void => {
    const addedLines = variantLineItemsMap(horseVariants.map((horseVariant) => originalLineItems.current[horseVariant.id] || {
      ...horseVariant, id: null, horse_variant_id: horseVariant.id,
      quantity_sent: 0, quantity_received: 0, currently_available: null,
    }));
    setFormattedTransferOrderLineItems((lineItems) => appendVariantLineItems(lineItems, addedLines));
    setIsDirty(true);
    const unsavedLineItemsHorseVariantIds = horseVariants.filter(({ id }) => !originalLineItems.current[id]).map(({ id }) => id);
    if (unsavedLineItemsHorseVariantIds.length > 0) void request(
      (signal) => previewUnsavedTransferOrderLineItems(transferOrder.id, unsavedLineItemsHorseVariantIds, signal),
      (previews) => { setFormattedTransferOrderLineItems((lineItems) => Object.fromEntries(
        Object.entries(lineItems).map(([id, lineItem]) => [id, { ...lineItem, ...previews[id] }]),
      )); },
    );
  };

  const handleDiscardAction = (): void => {
    cancel();
    setTransferOrder(originalTransferOrder);
    setFormattedTransferOrderLineItems(originalLineItems.current);
    setIsDirty(false);
  };

  const handleSendAll = (): void => {
    const newFormattedTransferOrderLineItems = Object.values(
      formattedTransferOrderLineItems,
    ).reduce<TransferOrderLineItems>((acc, formattedTransferOrderLineItem) => {
      if (formattedTransferOrderLineItem.currently_available === null) {
        acc[formattedTransferOrderLineItem.horse_variant_id] = formattedTransferOrderLineItem;
        return acc;
      }
      const currently_available = Number(formattedTransferOrderLineItem.currently_available);
      acc[formattedTransferOrderLineItem.horse_variant_id] = {
        ...formattedTransferOrderLineItem,
        quantity_sent: currently_available < 0 ? 0 : currently_available,
      };
      return acc;
    }, {});
    setFormattedTransferOrderLineItems(newFormattedTransferOrderLineItems);
    setIsDirty(true);
  };

  const handleSaveDraft = (): Promise<void> => {
    cancel();
    setLoading(true);

    const payload = {
      transfer_order: {
        ...transferOrder,
        transfer_order_horse_variants_attributes: getPayload(formattedTransferOrderLineItems),
        removed_horse_variant_ids: removedVariantIds(originalLineItems.current, formattedTransferOrderLineItems),
        state: "draft",
      },
    };
    return updateTransferOrder(transferOrder.id, payload)
      .then((response) => {
        acceptResponse(response);
        shopify.toast.show(SUCCESS_MESSAGES[UPDATE_TRANSFER_ORDER]);
        setIsDirty(false);
      })
      .catch((err: unknown) => {
        Rollbar.error(err, payload);
        const message = extractMessageFromError(err);
        setErrorMessage(message);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const tohvArray: TransferOrderLineItem[] = sortedVariantLineItems(formattedTransferOrderLineItems);
  const total = tohvArray.reduce(
    (acc, formattedTransferOrderLineItem) => {
      acc.cost +=
        assertNumber(formattedTransferOrderLineItem.quantity_sent) * assertNumber(formattedTransferOrderLineItem.cost);
      acc.quantity += assertNumber(formattedTransferOrderLineItem.quantity_sent);
      return acc;
    },
    { quantity: 0, cost: 0 },
  );

  const firstSelectableDate = useRef(new Date());

  const emptyState = (
    <EmptyState
      action={{
        content: "Add variants",
        onAction: () => {
          setAddVariantModalActive(true);
        },
      }}
      heading="No variants added"
      image=""
    >
      <p>You haven&apos;t added any variants to your transfer order yet.</p>
    </EmptyState>
  );

  const navigate = useNavigate();

  const pageMarkup = (
    <Page
      actionGroups={[
        {
          title: "CSV",
          actions: [
            {
              content: "Import",
              disabled: isDirty,
              onAction: (): void => {
                setImportCSVModalIsOpen(true);
              },
              icon: ImportIcon,
            },
            {
              content: "Export",
              onAction: async (): Promise<void> => {
                const transferOrderName = transferOrder.label || transferOrder.id;
                const filename = `Horse Transfer Order ${transferOrderName}.csv`;
                await downloadFile(`/transfer_orders/${transferOrder.id}.csv${window.location.search}`, filename);
              },
              icon: ExportIcon,
            },
          ],
        },
      ]}
      backAction={{
        content: "Transfer orders",
        onAction: async () => {
          await shopify.saveBar.leaveConfirmation();

          navigate("/transfer_orders");
        },
      }}
      primaryAction={{
        content: "Mark as sent",
        onAction: () => {
          setMarkAsSentModalActive(true);
        },
      }}
      title="Draft transfer order"
      titleMetadata={<Badge tone="info">Draft</Badge>}
    >
      <SaveBar disabled={loading} handleDiscard={handleDiscardAction} handleSave={handleSaveDraft} isDirty={isDirty} />

      <ConfirmationDialog
        active={markAsSentModalActive}
        cancelText="No"
        content="Are you sure you want to mark this transfer order as sent? You cannot undo this."
        okText="Yes"
        onCancel={() => {
          setMarkAsSentModalActive(false);
        }}
        onClose={() => {
          setMarkAsSentModalActive(false);
        }}
        onOk={() => {
          setMarkAsSentModalActive(false);
          handleMarkAsSent();
        }}
        title="Mark as sent"
      />
      <Layout>
        <Layout.Section>
          <ErrorBanner errorMessage={errorMessage} setErrorMessage={setErrorMessage} />
        </Layout.Section>
        <Layout.Section>
          <LegacyCard>
            <ImportFile
              apiCallMethod={async (formData: object) => importTransferOrderLineItems(transferOrder.id, formData)}
              modalIsOpen={importCSVModalIsOpen}
              setApiResponse={(newTOHV) => {
                const lineItems = variantLineItemsMap(newTOHV);
                originalLineItems.current = lineItems;
                setFormattedTransferOrderLineItems(lineItems);
                onSave({ origin, destination, transferOrder: initialTransferOrder, lineItems: newTOHV });
              }}
              setErrorMessage={setErrorMessage}
              setModalIsOpen={setImportCSVModalIsOpen}
              title="Import transfer order CSV"
            />
            <LegacyCard.Section title="Origin">
              <Label id="toOriginName">
                <HorseLink url={`/horse_locations/${origin.id}`}>{origin.name}</HorseLink>
              </Label>
            </LegacyCard.Section>
            <LegacyCard.Section title="Destination">
              <Label id="toDestinationName">
                <HorseLink url={`/horse_locations/${destination.id}`}>{destination.name}</HorseLink>
              </Label>
            </LegacyCard.Section>
            <LegacyCard.Section>
              <FormLayout>
                <FormLayout.Group condensed>
                  <TextField
                    autoComplete="off"
                    label="Label"
                    onChange={handleLabelChange}
                    placeholder="64568793"
                    value={transferOrder.label || ""}
                  />
                  <CustomDatePicker
                      date={transferOrder.estimated_arrival_date}
                    disabled={loading}
                    firstSelectableDate={firstSelectableDate.current}
                    label="Estimated arrival date"
                    placeholder="2021-07-01"
                    setDate={handleDateChange}
                  />
                </FormLayout.Group>
              </FormLayout>
            </LegacyCard.Section>
            <LegacyCard.Section title="Add variants">
              <LegacyStack alignment="center" distribution="center">
                <AddVariantDialog
                  active={addVariantModalActive}
                  disabled={loading}
                  initialHorseLocationId={origin.id}
                  initialQuantity={initialQuantity}
                  onAdd={addVariants}
                  onRemove={removeVariants}
                  primary
                  selectedVariantIds={Object.values(formattedTransferOrderLineItems).map(({ horse_variant_id }) => horse_variant_id)}
                  setActive={setAddVariantModalActive}
                  setErrorMessage={setErrorMessage}
                />
              </LegacyStack>
            </LegacyCard.Section>
          </LegacyCard>

          <LegacyCard>
            <Box position="relative">
              <Box insetBlockStart="200" insetInlineEnd="2400" position="absolute" zIndex="1">
                <Button onClick={handleSendAll}>Send all</Button>
              </Box>
              <TableControls
                choices={optionalColumns.map((column) => ({ value: column.id, label: column.title }))}
                disabled={isDirty}
                onChange={setSelectedColumns}
                selected={selectedColumns}
              />
            </Box>
            <IndexTable
              emptyState={emptyState}
              headings={[
                { title: "Variant" },
                ...visibleColumns,
                { title: "Available" },
                { title: "Quantity" },
                { title: "Unit cost" },
                { title: "Total value" },
                { title: "" },
              ]}
              itemCount={tohvArray.length}
              loading={loading}
              selectable={false}
            >
              <IndexTable.Row id="subHeaderTotalsRow" key={0} position={0} rowType="subheader">
                <IndexTable.Cell>Totals</IndexTable.Cell>
                {visibleColumns.map((column) => <IndexTable.Cell key={column.id} />)}
                <IndexTable.Cell />
                <IndexTable.Cell>{commaDelimiter(total.quantity)}</IndexTable.Cell>
                <IndexTable.Cell />
                <IndexTable.Cell>{formatMoney(total.cost || 0, userCurrency)}</IndexTable.Cell>
                <IndexTable.Cell />
              </IndexTable.Row>

              {tohvArray.map((variant, index) => (
                <IndexTable.Row id={variant.horse_variant_id.toString()} key={variant.horse_variant_id} position={index + 1}>
                  <IndexTable.Cell>
                    <HorseVariant key={variant.horse_variant_id} variant={variant} />
                  </IndexTable.Cell>
                  {visibleColumns.map((column) => (
                    <IndexTable.Cell key={column.id}>{variant[column.id]}</IndexTable.Cell>
                  ))}
                  <IndexTable.Cell>{variant.tracked ? variant.currently_available : "∞"}</IndexTable.Cell>
                  <IndexTable.Cell>
                    <div className="w120">
                      <TextField
                        autoComplete="off"
                        error={isDirty ? !variant.quantity_sent : null}
                        id={variant.horse_variant_id.toString()}
                        key={variant.horse_variant_id}
                        label=""
                        name="value"
                        onChange={handleQuantityChange}
                        type="number"
                        value={assertString(variant.quantity_sent)}
                      />
                    </div>
                  </IndexTable.Cell>
                  <IndexTable.Cell>{formatMoney(variant.cost, userCurrency)}</IndexTable.Cell>
                  <IndexTable.Cell>
                    {formatMoney(Number(variant.cost || 0) * Number(variant.quantity_sent || 0), userCurrency)}
                  </IndexTable.Cell>
                  <IndexTable.Cell>
                    <Button icon={<Icon source={XIcon} />} key={variant.horse_variant_id} onClick={() => { removeVariants([variant.horse_variant_id]); }} />
                  </IndexTable.Cell>
                </IndexTable.Row>
              ))}
            </IndexTable>
          </LegacyCard>
        </Layout.Section>
      </Layout>
      <PageActions
        secondaryActions={[
          {
            content: "Delete",
            destructive: true,
            onAction: (): void => {
              setDeletionDialogueActive(true);
            },
          },
        ]}
      />
      <DeleteConfirmationDialog
        active={deletionDialogueActive}
        content="Are you sure you want to delete this transfer? This action cannot be reversed."
        deleteItem={deleteTransferOrder}
        gotoPath="/transfer_orders"
        itemId={transferOrder.id}
        setActive={setDeletionDialogueActive}
        title="Delete transfer"
        toastMessage="Transfer order deleted"
      />
      <Footer pageTitle="transfer orders" url={TRANSFER_ORDER_HELP_PAGE_URL} />
    </Page>
  );

  return <EditorContent disabled={loading}>{pageMarkup}</EditorContent>;
}
